import React, { useState, useEffect, useRef, useMemo } from "react";
import classes from "./TextMoreLess.scss";
import PropTypes from "prop-types";
import I18nHOC from "UIComponents/I18nHOC";
import {
  getStringAutoLinks,
  getStyleStrippedText,
  decodeHTMLEntities,
} from "Utils";
import { Button } from "@toddle-edu/ds-web";

const TextMoreLess = ({
  value = "",
  minHeight = 64,
  lineClamp = 7,
  customTextStyle = { marginTop: 8 },
  containerDivStyle = {},
  t,
  testId = "",
  buttonProps = {},
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);
  const measureRef = useRef(null);

  const processedHtml = useMemo(() => {
    if (!value) return "";
    return decodeHTMLEntities(
      getStyleStrippedText({
        text: getStringAutoLinks(value),
        type: "SANITIZE_DANGER_HTML_PARAGRAPH",
      })
    );
  }, [value]);

  useEffect(() => {
    if (measureRef.current) {
      const fullHeight = measureRef.current.scrollHeight;
      setShouldShowToggle(fullHeight > minHeight);
    }
  }, [value, minHeight]);

  const toggleCollapsed = e => {
    e.stopPropagation();
    e.preventDefault();
    setCollapsed(prev => !prev);
  };

  if (!value) return null;

  const isContentTruncated = collapsed && shouldShowToggle;
  const textStyle = {
    position: "absolute",
    visibility: "hidden",
    height: "auto",
    width: "100%",
    pointerEvents: "none",
    zIndex: -1,
  };

  const stopPropagation = e => {
    e.stopPropagation();
  };

  const containerStyle = { ...containerDivStyle, position: "relative" };

  const truncatedStyle = isContentTruncated
    ? {
        display: "-webkit-box",
        WebkitLineClamp: lineClamp,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }
    : {};

  return (
    <div style={containerStyle} data-test-id={testId}>
      <div
        ref={measureRef}
        style={textStyle}
        dangerouslySetInnerHTML={{ __html: processedHtml }}
      />
      <div style={truncatedStyle}>
        <span dangerouslySetInnerHTML={{ __html: processedHtml }} />
      </div>

      {shouldShowToggle && collapsed && (
        <span onClick={stopPropagation}>
          <Button
            dsVersion="2.0"
            onClick={toggleCollapsed}
            className={classes.showMoreText}
            style={customTextStyle}
            type="inline"
            variant="progressive"
            testId={`${testId}-showmore`}
            {...buttonProps}
          >
            {t("see_more_noDots")}
          </Button>
        </span>
      )}
      {shouldShowToggle && !collapsed && (
        <span onClick={stopPropagation}>
          <Button
            dsVersion="2.0"
            onClick={toggleCollapsed}
            className={classes.showLessText}
            style={customTextStyle}
            type="inline"
            variant="progressive"
            testId={`${testId}-showless`}
            {...buttonProps}
          >
            {t("see_less_noDots")}
          </Button>
        </span>
      )}
    </div>
  );
};

TextMoreLess.propTypes = {
  value: PropTypes.string,
  minHeight: PropTypes.number,
  customTextStyle: PropTypes.object,
  containerDivStyle: PropTypes.object,
  testId: PropTypes.string,
  buttonProps: PropTypes.object,
};

export default I18nHOC({ resource: "common" })(TextMoreLess);
